import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import postsData from "../../posts/posts.json";
import "./Post.css";
import "highlight.js/styles/tokyo-night-dark.css";

export const Post = () => {
    const { id } = useParams();
    const [post, setPost] = useState(null);
    const [content, setContent] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadPost = async () => {
            setLoading(true);
            setError(null);

            // Find post metadata
            const postMeta = postsData.find((p) => p.id === id);
            if (!postMeta) {
                setError("Post not found");
                setLoading(false);
                return;
            }

            setPost(postMeta);

            // Load markdown content
            try {
                const response = await fetch(
                    `/content/posts/${postMeta.file}`,
                );
                if (!response.ok) {
                    throw new Error("Failed to load post content");
                }
                const text = await response.text();
                setContent(text);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        loadPost();
    }, [id]);

    if (loading) {
        return (
            <div className="post-page">
                <div className="loading">
                    <span className="prompt">$</span>
                    <span className="command">Loading post...</span>
                    <span className="cursor"></span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="post-page">
                <div className="error">
                    <h2>Error: {error}</h2>
                    <Link to="/posts" className="back-link">
                        ← Back to posts
                    </Link>
                </div>
            </div>
        );
    }

    const postUrl = `https://mustardtiger.xyz/posts/${id}`;
    const ogImage = "https://mustardtiger.xyz/og-image.png";

    return (
        <div className="post-page">
            <Helmet>
                <title>{post.title} - mustardtiger.xyz</title>
                <meta name="description" content={post.excerpt} />

                {/* Open Graph Tags */}
                <meta property="og:type" content="article" />
                <meta property="og:url" content={postUrl} />
                <meta property="og:title" content={post.title} />
                <meta property="og:description" content={post.excerpt} />
                <meta property="og:image" content={ogImage} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />

                {/* Article-specific Open Graph Tags */}
                <meta property="article:published_time" content={post.date} />
                <meta property="article:author" content={post.author} />
                {post.tags.map((tag) => (
                    <meta key={tag} property="article:tag" content={tag} />
                ))}

                {/* Twitter Card Tags */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:url" content={postUrl} />
                <meta name="twitter:title" content={post.title} />
                <meta name="twitter:description" content={post.excerpt} />
                <meta name="twitter:image" content={ogImage} />
            </Helmet>
            <div className="post-navigation">
                <Link to="/posts" className="back-link">
                    ← Back to posts
                </Link>
            </div>

            <article className="post-content">
                <header className="post-content-header">
                    <div className="prompt-line">
                        <span className="prompt">$</span>
                        <span className="command">cat posts/{post.file}</span>
                    </div>
                    <div className="post-metadata">
                        <span className="metadata-item">
                            <span className="metadata-label">Date:</span>
                            <span className="metadata-value">{post.date}</span>
                        </span>
                        <span className="metadata-item">
                            <span className="metadata-label">Author:</span>
                            <span className="metadata-value">
                                {post.author}
                            </span>
                        </span>
                        <span className="metadata-item">
                            <span className="metadata-label">Tags:</span>
                            <span className="metadata-value">
                                {post.tags.map((tag) => (
                                    <span key={tag} className="tag">
                                        #{tag}
                                    </span>
                                ))}
                            </span>
                        </span>
                    </div>
                </header>

                <div className="markdown-content">
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeHighlight]}
                    >
                        {content}
                    </ReactMarkdown>
                </div>

                <footer className="post-footer">
                    <div className="footer-line">
                        <span className="prompt">$</span>
                        <span className="command">EOF</span>
                    </div>
                    <Link to="/posts" className="back-link">
                        ← Back to all posts
                    </Link>
                </footer>
            </article>
        </div>
    );
};
